import { XyzPoint } from 'cd-eft/models/SurveyEngineering/XyzPoint';
import { esriGeometryEngine, esriProjection, MapView, Point, SpatialReference } from 'cd-eft/utils/esriTypes';
import { IDisposable } from 'cd-eft/framework/Disposer';
import {
  ElevationInterpolater,
  getDistance2D,
  getSlopePercentage,
} from 'cd-eft/components/SurveyEngineering/Maps/SETMapFunctions/elevationInterpolationEngine';
import stores from '..';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import { AppConstants } from 'cd-eft/appConstants';
import Multipoint from '@arcgis/core/geometry/Multipoint';
import { convertLinearUnit } from 'cd-eft/utils/structureLayerUtils';
import { getElevationUsingContours } from 'cd-eft/components/Design/DesignContainer/DesignContainerVM';
import { getBoundaryPolygon, isPointInsidePolygon } from 'cd-eft/utils/gisUtils';
import { SetupInterpolateTinElevation } from 'cd-eft/framework/TinContourSurface/TinContourSurface';
import { showCoordinates } from 'cd-eft/components/SurveyEngineering/Maps/SETMapFunctions/loadLayers';

const { designStore } = stores.AppStore;

class MapTooltipProvider implements IDisposable {
  private _currentVertex: XyzPoint | undefined = void 0;
  private _mapView: MapView | undefined = void 0;
  private _viewPointerMoveHandle;
  private _surveyPointLayer: GraphicsLayer | undefined;
  private _allGroundPointsMP: Multipoint = new Multipoint({ hasZ: true });
  private _spatialReference: SpatialReference;

  constructor(mapView: MapView, projectSR: SpatialReference) {
    this._mapView = mapView;
    this._spatialReference = projectSR;
    if (this._mapView) {
      if (designStore.currentSurface.surfaceDefClassName === 'survey') {
        this._surveyPointLayer = this._mapView.map?.findLayerById(AppConstants.SurveyPointsLayerId) as GraphicsLayer;
        if (this._surveyPointLayer && this._surveyPointLayer instanceof GraphicsLayer) {
          this._allGroundPointsMP = this.getSurveyPoints(this._surveyPointLayer);
        }
      }
    }
  }

  private getSurveyPoints(pointLayer) {
    const pointsArray = pointLayer.graphics.items;
    const multipoint: Multipoint = new Multipoint({ spatialReference: this._spatialReference });
    for (let i = 0; i < pointsArray.length; i++) {
      const geom = pointsArray[i].geometry;
      const point = new Point({
        x: geom.x,
        y: geom.y,
        z: convertLinearUnit(geom.z, 'meters', 'intl-feet'),
        hasZ: true,
        spatialReference: { wkid: geom.spatialReference.wkid }
      })
      const projectedPoint: any = esriProjection.project(point as Point, this._spatialReference);
      multipoint.addPoint(projectedPoint);
    }
    return multipoint;
  }

  public get currentVertex(): XyzPoint | undefined {
    return this._currentVertex;
  }
  public set currentVertex(value: XyzPoint | undefined) {
    this._currentVertex = value;
  }

  public start() {
    this._currentVertex = void 0;
    if (designStore.coordinateWatchHandler) designStore.coordinateWatchHandler.remove();
    designStore.coordinateWatchHandler = undefined;
    if (this._mapView) this._viewPointerMoveHandle = this._mapView.on('pointer-move', this.viewPointerMoveHandler);
  }

  public stop() {
    this._currentVertex = void 0;
    this._viewPointerMoveHandle?.remove();
    SetupInterpolateTinElevation(designStore.currentSurface.surfaceDefClassName, this._mapView);
  }

  private viewPointerMoveHandler = async (event: __esri.ViewPointerMoveEvent) => {
    const movingPoint = this._mapView?.toMap(event); // Web Mercator
    if (movingPoint && this._mapView) {
      const dataPoint: Point = esriProjection.project(
        movingPoint,
        this._spatialReference
      ) as Point; // Custom projection

      if (designStore.currentSurface.surfaceDefClassName === 'survey') {
        this.handleSurveySurface(dataPoint, movingPoint);
      } else { // DEM surface
        await this.handleDEMSurface(dataPoint, movingPoint);
      }
    }
  };

  private handleSurveySurface(dataPoint, movingPoint) {
    if (designStore.snappingEnabled) {
      const findResult = esriGeometryEngine.nearestVertex(this._allGroundPointsMP, dataPoint);
      if (findResult.distance < 21) {
        const index = findResult.vertexIndex;
        const z = Number(this._allGroundPointsMP.points[index][2].toFixed(2));
        if (this._currentVertex) {
          const currentPoint = new XyzPoint(findResult.coordinate.x, findResult.coordinate.y, z);
          const distanceBtwPoints = getDistance2D(this._currentVertex, currentPoint);
          const slopePercentage = getSlopePercentage(this._currentVertex, currentPoint);
          if (distanceBtwPoints === 0) {
            showCoordinates({x: findResult.coordinate.x, y: findResult.coordinate.y, z: z}, {pointName: designStore.surveyPointNames[index]});
          } else {
            showCoordinates({x: findResult.coordinate.x, y: findResult.coordinate.y, z: z}, {d: distanceBtwPoints.toFixed(2), s: slopePercentage.toFixed(2), pointName: designStore.surveyPointNames[index]});
          }
        } else {
          showCoordinates({x: findResult.coordinate.x, y: findResult.coordinate.y, z: z}, {pointName: designStore.surveyPointNames[index]});
        }
      } else {
        this.handleSurveyNoSnapping(dataPoint, movingPoint);
      }
    } else {
      this.handleSurveyNoSnapping(dataPoint, movingPoint);
    }
  }

  private handleSurveyNoSnapping(dataPoint, movingPoint) {
    const z = ElevationInterpolater.Current?.Interpolate(dataPoint.x, dataPoint.y, movingPoint);
    if (z) {
      if (this._currentVertex) {
        const currentPoint = new XyzPoint(dataPoint.x, dataPoint.y, z);
        const distanceBtwPoints = getDistance2D(this._currentVertex, currentPoint);
        const slopePercentage = getSlopePercentage(this._currentVertex, currentPoint);
        showCoordinates({x: dataPoint.x, y: dataPoint.y, z: z}, {d: distanceBtwPoints.toFixed(2), s: slopePercentage.toFixed(2)});
      } else {
        showCoordinates({x: dataPoint.x, y: dataPoint.y, z: z});
      }
    } else {
      if (this._currentVertex) {
        const currentPoint = new XyzPoint(dataPoint.x, dataPoint.y, -1);
        const distanceBtwPoints = getDistance2D(this._currentVertex, currentPoint);
        showCoordinates({x: dataPoint.x, y: dataPoint.y}, {d: distanceBtwPoints.toFixed(2)});
      } else {
        showCoordinates({x: dataPoint.x, y: dataPoint.y});
      }
    }
  }

  private async handleDEMSurface(dataPoint, movingPoint) {
    if (this._mapView) {
      const bPolygon = getBoundaryPolygon(this._spatialReference);
      const valid = await isPointInsidePolygon(dataPoint.x, dataPoint.y, bPolygon, this._spatialReference);
      let z;
      if (valid) {
        z = await getElevationUsingContours(dataPoint, movingPoint, this._mapView);
      }
      if (z) {
        if (this._currentVertex) {
          const currentPoint = new XyzPoint(dataPoint.x, dataPoint.y, z);
          const distanceBtwPoints = getDistance2D(this._currentVertex, currentPoint);
          const slopePercentage = getSlopePercentage(this._currentVertex, currentPoint);
          showCoordinates({x: dataPoint.x, y: dataPoint.y, z: z}, {d: distanceBtwPoints.toFixed(2), s: slopePercentage.toFixed(2)});
        } else {
          showCoordinates({x: dataPoint.x, y: dataPoint.y, z: z});
        }
      } else {
        if (this._currentVertex) {
          const currentPoint = new XyzPoint(dataPoint.x, dataPoint.y, -1);
          const distanceBtwPoints = getDistance2D(this._currentVertex, currentPoint);
          showCoordinates({x: dataPoint.x, y: dataPoint.y}, {d: distanceBtwPoints.toFixed(2)});
        } else {
          showCoordinates({x: dataPoint.x, y: dataPoint.y});
        }
      }
    }
  }

  Dispose() {
    this.stop();
  }
}

export { MapTooltipProvider };
